import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

const submitSchema = z.object({
  slug: z.string().min(1).max(120),
  name: z.string().trim().max(40).optional().nullable(),
  stars: z.number().int().min(1).max(5),
  body: z.string().trim().min(5).max(1200),
  honeypot: z.string().optional(),
});

const listSchema = z.object({ slug: z.string().min(1).max(120) });

export type PublicUserReview = {
  id: string;
  name: string;
  stars: number;
  body: string;
  created_at: string;
};

export const listUserAppReviews = createServerFn({ method: "GET" })
  .inputValidator((d) => listSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("app_user_reviews")
      .select("id, name, stars, body, created_at")
      .eq("app_slug", data.slug)
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) return { rows: [] as PublicUserReview[] };
    return { rows: (rows ?? []) as PublicUserReview[] };
  });

export const submitUserAppReview = createServerFn({ method: "POST" })
  .inputValidator((d) => submitSchema.parse(d))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.length > 0) return { ok: true, id: null };
    const ip =
      (getRequestHeader("x-forwarded-for") || getRequestHeader("cf-connecting-ip") || "")
        .split(",")[0]
        .trim() || "0.0.0.0";
    const buf = new TextEncoder().encode(`ip:${ip}`);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    const ip_hash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 32);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: id, error } = await supabaseAdmin.rpc("app_review_submit", {
      _app_slug: data.slug,
      _name: data.name?.trim() || "Anonymous",
      _stars: data.stars,
      _body: data.body,
      _ip_hash: ip_hash,
    } as never);
    if (error) {
      const msg = error.message || "";
      if (msg.includes("rate_limited")) return { ok: false, error: "rate_limited" };
      return { ok: false, error: "submit_failed" };
    }
    return { ok: true, id: id as unknown as string };
  });
